import api from './axiosConfig';
import CustomEventDispatcher from './CustomEventDispatcher';

class OrderConfirmationService extends CustomEventDispatcher {

  constructor() {
    super()
    this.response = {};
  }

  async getOrderConfirmationById(id) {
    const url = `/api/orderconfirmation/${id}`;
    try {
      const response = await api.get(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const orderConfirmation = response.data.data;
        // Voeg XML en JSON data toe in juiste format voor de frontend
        orderConfirmation.xml_data = orderConfirmation.input || '';
        orderConfirmation.json_data = orderConfirmation.output ? JSON.parse(orderConfirmation.output) : null;

        const event = new CustomEvent('orderConfirmationLoaded', { detail: orderConfirmation });
        setTimeout(() => {
          this.dispatchEvent(event);
        }, 100);
        return orderConfirmation;
      } else {
        throw new Error(response.data.message || 'Onbekende fout bij het ophalen van de orderbevestiging');
      }
    } catch (error) {
      console.error('Er is een fout opgetreden in getOrderConfirmationById():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('getOrderConfirmationFailed', error.response ? error.response.data : error);
      }
      throw error;
    }
  }

  async getAllOrderConfirmationStatuses() {
    const url = `/api/orderconfirmation/statuses`;
    try {
      const response = await api.get(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const statusses = response.data.data;
        const event = new CustomEvent('orderConfirmationStatussenLoaded', { detail: statusses });
        setTimeout(() => {
          this.dispatchEvent(event);
        }, 100);
        return statusses;
      } else {
        throw new Error(response.data.message || 'Onbekende fout bij het ophalen van orderbevestigingsstatussen');
      }
    } catch (error) {
      console.error('Er is een fout opgetreden in getAllOrderConfirmationStatuses():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('getOrderConfirmationStatussesFailed', error.response ? error.response.data : error);
      }
      throw error;
    }
  }

  async updateOrderConfirmationStatus(orderConfirmationId, statusId) {
    const url = `/api/orderconfirmation/${orderConfirmationId}/status`;
    try {
      const response = await api.put(url, { statusId }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const updatedOrderConfirmation = response.data.data;
        const event = new CustomEvent('orderConfirmationStatusUpdated', {
          detail: { id: orderConfirmationId, status_id: statusId }
        });
        setTimeout(() => {
          this.dispatchEvent(event);
        }, 100);
        return { success: true, data: updatedOrderConfirmation };
      } else {
        return {
          success: false,
          message: response.data.message || 'Onbekende fout bij het bijwerken van de status'
        };
      }
    } catch (error) {
      console.error('Er is een fout opgetreden in updateOrderConfirmationStatus():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('updateOrderConfirmationStatusFailed', error.response ? error.response.data : error);
      }
      return { success: false, message: error.response ? error.response.data.message : error.message };
    }
  }

  async updateOrderConfirmationReference(orderConfirmationId, reference) {
    const url = `/api/orderconfirmation/${orderConfirmationId}/reference`;
    try {
      const response = await api.put(url, { reference }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const event = new CustomEvent('orderConfirmationReferenceUpdated', {
          detail: { id: orderConfirmationId, reference: reference }
        });
        setTimeout(() => {
          this.dispatchEvent(event);
        }, 100);
        return { success: true, data: response.data.data };
      } else {
        return {
          success: false,
          message: response.data.message || 'Onbekende fout bij het bijwerken van de referentie'
        };
      }
    } catch (error) {
      console.error('Er is een fout opgetreden in updateOrderConfirmationReference():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('updateOrderConfirmationReferenceFailed', error.response ? error.response.data : error);
      }
      return { success: false, message: error.response ? error.response.data.message : error.message };
    }
  }

  async updateOrderConfirmationXml(orderConfirmationId, xmlData) {
    const url = `/api/orderconfirmation/${orderConfirmationId}/xml`;
    try {
      const response = await api.put(url, { xmlData }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const event = new CustomEvent('orderConfirmationXmlUpdated', {
          detail: { id: orderConfirmationId }
        });
        setTimeout(() => {
          this.dispatchEvent(event);
        }, 100);
        return { success: true, data: response.data.data };
      } else {
        return {
          success: false,
          message: response.data.message || 'Onbekende fout bij het bijwerken van de XML data'
        };
      }
    } catch (error) {
      console.error('Er is een fout opgetreden in updateOrderConfirmationXml():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('updateOrderConfirmationXmlFailed', error.response ? error.response.data : error);
      }
      return { success: false, message: error.response ? error.response.data.message : error.message };
    }
  }

  async retryOrderConfirmation(orderConfirmationId, newOutput) {
    const url = `/api/orderconfirmation/${orderConfirmationId}/retry`;
    try {
      const response = await api.put(url, newOutput, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Er is een fout opgetreden in retryOrderConfirmation():', error);
      if (error.response && error.response.status === 401) {
        this._dispatchUnauthorized();
      } else {
        this._dispatchCustomEvent('retryOrderConfirmationFailed', error.response ? error.response.data : error);
      }
      return { success: false, message: error.response ? (error.response.data?.message || 'Request failed') : error.message };
    }
  }

  _dispatchUnauthorized() {
    const event = new CustomEvent('unauthorized');
    this.dispatchEvent(event);
  }

  _dispatchCustomEvent(name, detail) {
    const event = new CustomEvent(name, { detail: detail || {} });
    this.dispatchEvent(event);
  }
}

export default OrderConfirmationService;
